import React, { useState } from "react";
import { UseFormRegister } from "react-hook-form";
import { FlexAlign } from "../_ui/FlexAlign";

export interface RequirementsType {
  TEXT?: { length: number };
  PHOTO?: {
    minetype: string[];
    max_filesize: number;
    max_items?: number;
    min_aspect_ratio?: number;
    max_aspect_ratio?: number;
    max_resolution?: number;
    overrides?: {
      [key: string]: {
        minetype: string[];
        max_filesize: number;
      };
    };
  };
  VIDEO?: {
    minetype: string[];
    max_filesize: number;
    max_items?: number;
    min_duration?: number;
    max_duration?: number;
    overrides?: {
      [key: string]: {
        minetype: string[];
        max_duration: number;
        max_filesize: number;
        min_duration: number;
      };
    };
  };
  total_max_items?: number;
}

interface RequirementsProps {
  register: UseFormRegister<any>;
  defaultValues?: RequirementsType;
}

export const Requirements: React.FC<RequirementsProps> = ({
  register,
  defaultValues,
}) => {
  const [videoOverrides, setVideoOverrides] = useState<{
    [key: string]: {
      minetype: string[];
      max_duration: number;
      max_filesize: number;
      min_duration: number;
    };
  }>(defaultValues?.VIDEO?.overrides || {});

  const [photoOverrides, setPhotoOverrides] = useState<{
    [key: string]: {
      minetype: string[];
      max_filesize: number;
    };
  }>(defaultValues?.PHOTO?.overrides || {});

  const [editingTypes, setEditingTypes] = useState<{ [key: string]: string }>(
    {}
  );

  const addVideoOverride = () => {
    const type = "NEW-OVERRIDE-TYPE";

    const newOverride = {
      minetype: ["video/mp4"],
      max_duration: 60,
      max_filesize: 1000,
      min_duration: 3,
    };
    setVideoOverrides({ ...videoOverrides, [type]: newOverride });
    setEditingTypes({ ...editingTypes, [type]: type });
  };

  const addPhotoOverride = () => {
    const type = "NEW-OVERRIDE-TYPE";

    const newOverride = {
      minetype: ["image/jpeg"],
      max_filesize: 4,
    };
    setPhotoOverrides({ ...photoOverrides, [type]: newOverride });
    setEditingTypes({ ...editingTypes, [type]: type });
  };

  const removeVideoOverride = (type: string) => {
    const newOverrides = { ...videoOverrides };
    delete newOverrides[type];
    setVideoOverrides(newOverrides);

    const newEditingTypes = { ...editingTypes };
    delete newEditingTypes[type];
    setEditingTypes(newEditingTypes);
  };

  const removePhotoOverride = (type: string) => {
    const newOverrides = { ...photoOverrides };
    delete newOverrides[type];
    setPhotoOverrides(newOverrides);

    const newEditingTypes = { ...editingTypes };
    delete newEditingTypes[type];
    setEditingTypes(newEditingTypes);
  };

  const handleTypeChange = (
    oldType: string,
    newType: string,
    isVideo: boolean
  ) => {
    if (!newType || oldType === newType) return;

    if (isVideo) {
      const newOverrides = { ...videoOverrides };
      const override = newOverrides[oldType];
      delete newOverrides[oldType];
      newOverrides[newType] = override;
      setVideoOverrides(newOverrides);
    } else {
      const newOverrides = { ...photoOverrides };
      const override = newOverrides[oldType];
      delete newOverrides[oldType];
      newOverrides[newType] = override;
      setPhotoOverrides(newOverrides);
    }

    const newEditingTypes = { ...editingTypes };
    delete newEditingTypes[oldType];
    setEditingTypes(newEditingTypes);
  };

  return (
    <div className="tabs tabs-border mb-4">
      <div role="tablist" className="tabs tabs-boxed">
        <input
          type="radio"
          name="requirements_tabs"
          role="tab"
          className="tab"
          aria-label="Text"
          defaultChecked
        />
        <div role="tabpanel" className="tab-content pt-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Maximum Length</span>
            </label>
            <input
              type="number"
              placeholder="Maximum text length"
              {...register("requirements.TEXT.length")}
              className="input w-full"
            />
          </div>
        </div>

        <input
          type="radio"
          name="requirements_tabs"
          role="tab"
          className="tab"
          aria-label="Photos"
        />
        <div role="tabpanel" className="tab-content pt-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">
                Allowed MIME Types (comma-separated)
              </span>
            </label>
            <input
              type="text"
              placeholder="Allowed MIME types"
              {...register("requirements.PHOTO.minetype")}
              className="input w-full"
            />
          </div>
          <FlexAlign className="mt-4">
            <>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Maximum File Size (MB)</span>
                </label>
                <input
                  type="number"
                  step="any"
                  placeholder="Maximum file size"
                  {...register("requirements.PHOTO.max_filesize")}
                  className="input w-full"
                />
              </div>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Maximum Items</span>
                </label>
                <input
                  type="number"
                  placeholder="Maximum number of photos"
                  {...register("requirements.PHOTO.max_items")}
                  className="input w-full"
                />
              </div>
            </>
          </FlexAlign>

          <FlexAlign className="mt-4">
            <>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">
                    Minimum Aspect Ratio (width / height)
                  </span>
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="e.g., 0.8"
                  {...register("requirements.PHOTO.min_aspect_ratio")}
                  className="input w-full"
                />
              </div>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">
                    Maximum Aspect Ratio (width / height)
                  </span>
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="e.g., 1.91"
                  {...register("requirements.PHOTO.max_aspect_ratio")}
                  className="input w-full"
                />
              </div>
            </>
          </FlexAlign>

          <div className="form-control mt-4">
            <label className="label">
              <span className="label-text">
                Maximum Image Edge Resolution (px)
              </span>
            </label>
            <input
              type="number"
              min="2"
              step="1"
              placeholder="e.g., 2048"
              {...register("requirements.PHOTO.max_resolution")}
              className="input w-full"
            />
          </div>

          {/* Photo Overrides */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold">Photo Overrides</h4>
              <button
                type="button"
                onClick={addPhotoOverride}
                className="btn btn-primary btn-sm"
              >
                Add Override
              </button>
            </div>
            {Object.entries(photoOverrides).map(([type, override]) => (
              <div key={type} className="border-t pt-4 mt-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Type</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., STORY"
                    defaultValue={type}
                    onBlur={(e) =>
                      handleTypeChange(type, e.target.value, false)
                    }
                    className="input w-full"
                  />
                </div>

                <div className="form-control mt-4">
                  <label className="label">
                    <span className="label-text">
                      Allowed MIME Types (comma-separated)
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder="Allowed MIME types"
                    defaultValue={(override.minetype ?? []).join(", ")}
                    {...register(
                      `requirements.PHOTO.overrides.${type}.minetype`
                    )}
                    className="input w-full"
                  />
                </div>
                <div className="form-control mt-4">
                  <label className="label">
                    <span className="label-text">Maximum File Size (MB)</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    placeholder="Maximum file size"
                    defaultValue={override.max_filesize}
                    {...register(
                      `requirements.PHOTO.overrides.${type}.max_filesize`
                    )}
                    className="input w-full"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removePhotoOverride(type)}
                  className="btn btn-neutral btn-sm mt-4"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <input
          type="radio"
          name="requirements_tabs"
          role="tab"
          className="tab"
          aria-label="Videos"
        />
        <div role="tabpanel" className="tab-content pt-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">
                Allowed MIME Types (comma-separated)
              </span>
            </label>
            <input
              type="text"
              placeholder="Allowed MIME types"
              {...register("requirements.VIDEO.minetype")}
              className="input w-full"
            />
          </div>
          <FlexAlign className="mt-4">
            <>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Maximum File Size (MB)</span>
                </label>
                <input
                  type="number"
                  step="any"
                  placeholder="Maximum file size"
                  {...register("requirements.VIDEO.max_filesize")}
                  className="input w-full"
                />
              </div>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Maximum Items</span>
                </label>
                <input
                  type="number"
                  placeholder="Maximum number of videos"
                  {...register("requirements.VIDEO.max_items")}
                  className="input w-full"
                />
              </div>
            </>
          </FlexAlign>
          <FlexAlign className="mt-4">
            <>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Minimum Duration (seconds)</span>
                </label>
                <input
                  type="number"
                  placeholder="Minimum duration"
                  {...register("requirements.VIDEO.min_duration")}
                  className="input w-full"
                />
              </div>
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Maximum Duration (seconds)</span>
                </label>
                <input
                  type="number"
                  placeholder="Maximum duration"
                  {...register("requirements.VIDEO.max_duration")}
                  className="input w-full"
                />
              </div>
            </>
          </FlexAlign>

          {/* Video Overrides */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold">Video Overrides</h4>
              <button
                type="button"
                onClick={addVideoOverride}
                className="btn btn-primary btn-sm"
              >
                Add Override
              </button>
            </div>
            {Object.entries(videoOverrides).map(([type, override]) => (
              <div key={type} className="border-t pt-4 mt-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Type</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., STORY"
                    defaultValue={type}
                    onBlur={(e) => handleTypeChange(type, e.target.value, true)}
                    className="input w-full"
                  />
                </div>

                <div className="form-control mt-4">
                  <label className="label">
                    <span className="label-text">
                      Allowed MIME Types (comma-separated)
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder="Allowed MIME types"
                    defaultValue={(override.minetype ?? []).join(", ")}
                    {...register(
                      `requirements.VIDEO.overrides.${type}.minetype`
                    )}
                    className="input w-full"
                  />
                </div>
                <FlexAlign className="mt-4">
                  <>
                    <div className="form-control w-full">
                      <label className="label">
                        <span className="label-text">
                          Maximum Duration (seconds)
                        </span>
                      </label>
                      <input
                        type="number"
                        placeholder="Maximum duration"
                        defaultValue={override.max_duration}
                        {...register(
                          `requirements.VIDEO.overrides.${type}.max_duration`
                        )}
                        className="input w-full"
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label">
                        <span className="label-text">
                          Maximum File Size (MB)
                        </span>
                      </label>
                      <input
                        type="number"
                        step="any"
                        placeholder="Maximum file size"
                        defaultValue={override.max_filesize}
                        {...register(
                          `requirements.VIDEO.overrides.${type}.max_filesize`
                        )}
                        className="input w-full"
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label">
                        <span className="label-text">
                          Minimum Duration (seconds)
                        </span>
                      </label>
                      <input
                        type="number"
                        placeholder="Minimum duration"
                        defaultValue={override.min_duration}
                        {...register(
                          `requirements.VIDEO.overrides.${type}.min_duration`
                        )}
                        className="input w-full"
                      />
                    </div>
                  </>
                </FlexAlign>
                <button
                  type="button"
                  onClick={() => removeVideoOverride(type)}
                  className="btn btn-neutral btn-sm mt-4"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <input
          type="radio"
          name="requirements_tabs"
          role="tab"
          className="tab"
          aria-label="All Items"
        />
        <div role="tabpanel" className="tab-content pt-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Maximum Total Items</span>
            </label>
            <input
              type="number"
              placeholder="Maximum total number of items (photos + videos)"
              {...register("requirements.total_max_items")}
              className="input w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
